import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

function Login() {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    username: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // ONLY trigger "required" error if input was already touched/blurred once
    if (errors[name]) {
      if (value.trim() === "") {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: `${name.charAt(0).toUpperCase() + name.slice(1)} is required`,
        }));
      } else {
        // Clear error if typing again
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "",
        }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const newErrors = { ...errors };

    if (!value.trim()) {
      newErrors[name] = `${
        name.charAt(0).toUpperCase() + name.slice(1)
      } is required`;
      setErrors(newErrors);
      return; // ✅ Stop further validation if empty
    }

    if (name === "username") {
      if (!/^[a-zA-Z0-9_]+$/.test(value)) {
        newErrors.username = "Only letters, numbers and underscores allowed";
      } else if (value.length < 3 || value.length > 50) {
        newErrors.username = "Username must be between 3-50 characters";
      } else {
        newErrors.username = "";
      }
    }

    if (name === "password") {
      if (value.length < 3 || value.length > 50) {
        newErrors.password = "Password must be 3-50 characters";
      } else {
        newErrors.password = "";
      }
    }

    setErrors(newErrors);
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      username: "",
      password: "",
    };

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
      isValid = false;
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = "Only letters, numbers and underscores allowed";
      isValid = false;
    } else if (formData.username.length < 3 || formData.username.length > 50) {
      newErrors.username = "Username must be between 3-50 characters";
      isValid = false;
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 3 || formData.password.length > 50) {
      newErrors.password = "Password must be 3-50 characters";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setError("");
    setLoading(true);

    try {
      const result = await login(formData.username, formData.password);

      if (!result) {
        setError(
          "Uh-oh! Something's not quite right with your username or password. Give it another try!"
        );
        return;
      }

      // Navigate immediately for both new and existing users
      navigate("/home");
    } catch (err) {
      console.error("Login error:", err);
      setError("Hmm... something went wrong. Let's give it another shot!");
    } finally {
      setLoading(false);
    }
  };

  // Remove the isNewUser state and related effects as they're no longer needed

  // 3. Update the useEffect to handle all authenticated cases
  useEffect(() => {
    if (state.isAuthenticated) {
      console.log("User authenticated. Navigating to dashboard...");
      navigate("/home");
    }
  }, [state.isAuthenticated, navigate]);

  return (
    <div className="main-content h-100 m-0 w-100">
      <div className="h-100">
        <div className="container-fluid h-100">
          <div className="row h-100">
            <div className="col-sm-12 col-mdrs-6 col-lg-7 col-xl-7">
              <div className="d-flex h-100 lc-jc-res align-lc">
                <div className="mw-500px txt-align-res">
                  <img
                    src="/assets/images/login.gif"
                    className="pb-2 img-width-200px"
                    alt="Login Animation"
                  />
                  <h1 className="fw-bold hide-on-420px ps-3 pb-2 pe-3 font-size-42 log-con-color font-family-popins">
                    Welcome back! Sign in to manage your database migrations
                    seamlessly.
                  </h1>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-mdrs-6 col-lg-5 col-xl-5 rs-m-5">
              <div className="d-flex h-100 jc-res align-rc">
                <div className="card card-detail" style={{ width: "100px" }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-center pb-3 pt-2">
                      <img
                        src="/assets/images/dark-logo.png"
                        className="img-width-100px"
                        alt="Logo"
                      />
                    </div>
                    <h4 className="fw-bold text-center font-family-popins">
                      {" "}
                      Sign In to DbShift{" "}
                    </h4>
                    <p className="mb-0 fw-bold display-on-420px text-center font-size-12">
                      Welcome back! Sign in to manage your database migrations
                      seamlessly.
                    </p>
                    <form className="pt-3" onSubmit={handleSubmit}>
                      <div className="mb-4">
                        <input
                          type="text"
                          name="username"
                          value={formData.username}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`form-control highlighted-input w-100 colorfor-placeholder styled-input font-size-12 ${
                            errors.username ? "is-invalid" : ""
                          }`}
                          placeholder="Enter your username"
                        />
                        {errors.username && (
                          <div className="text-danger font-size-10 mt-1">
                            {errors.username}
                          </div>
                        )}
                      </div>

                      <div className="mb-4">
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`form-control highlighted-input styled-input w-100 font-size-12 colorfor-placeholder ${
                            errors.password ? "is-invalid" : ""
                          }`}
                          placeholder="Enter your password"
                        />
                        {errors.password && (
                          <div className="text-danger font-size-10 mt-1">
                            {errors.password}
                          </div>
                        )}
                      </div>

                      {/* Error Message Container */}
                      {error && (
                        <div
                          className="error-msg font-size-12 fw-bold mb-4 "
                          style={{
                            // backgroundColor: "#fdecea",
                            backgroundColor: "F4D6DD",
                            //    " color: "#d32f2f","
                            color: "#A5525B",
                            padding: "10px 15px",
                            borderRadius: "8px",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                            marginBottom: "0.5rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "14px",
                            fontWeight: "500",
                            fontFamily: "inter, sans-serif",
                            animation: "fadeIn 0.3s ease-in-out",
                          }}
                        >
                          {/* ⚠️ {error} */}
                          {error}
                        </div>
                      )}

                      <button
                        type="submit"
                        className="btn text-white fw-bold w-100 h-45px btn-detail"
                        disabled={loading}
                      >
                        {loading ? "Signing in..." : "Sign In"}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
